import { Howl, Howler } from 'howler';
import * as THREE from 'three';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';

/**
 * Scene-specific + spatial audio for ORVAE (2026-07-18, from the user's brief). Three layers:
 *
 *  1. MUSIC BEDS — one looping track per chapter (section 1-5), crossfaded when the section
 *     changes. Each OGG is authored with fade head/tail so the loop seams smoothly.
 *  2. SPATIAL LOOPS — environmental point sources (water pool, the Orvae emblem) positioned in
 *     3D via Howler's PannerNode. They swell as the camera nears the mesh and fall silent far
 *     away, so a source is only heard when you're near it — the "immersive 3D" the user wanted.
 *     Plus a broad chapter-1 WIND bed (non-positional) that fades with the section.
 *  3. TRANSITION SWELL — a one-shot under the white flash at 75%, with fades.
 *
 * All on Howler's context, so master volume / mute / blur-mute apply. The listener (position +
 * orientation) tracks the camera every frame via {@link updateListener}.
 *
 * Nothing is loaded until {@link start} (called once audio can play, after the preloader), so the
 * heavy OGGs don't compete with the first paint. Browser autoplay still applies — Howler resumes
 * the context on the first gesture.
 */
interface SpatialVoice {
  howl: Howl;
  id: number | null;
  position: THREE.Vector3;
  hasPosition: boolean;
  cfg: (typeof settings.audio.scenes.spatial)[number];
}

export class SceneAudio {
  private started = false;
  private readonly loops = new Map<number, Howl>();
  private activeSection = -1;
  private readonly spatial: SpatialVoice[] = [];
  private wind: Howl | null = null;
  private transition: Howl | null = null;
  /** Frame-cue one-shots (lazily created) + their armed state — see {@link checkCues}. */
  private readonly cueHowls = new Map<string, Howl>();
  private readonly cueArmed: boolean[] = [];
  private readonly meshPositions = new Map<string, THREE.Vector3>();
  private readonly log = createLogger('scene-audio');

  private readonly _fwd = new THREE.Vector3();
  private readonly _up = new THREE.Vector3();
  private readonly _pos = new THREE.Vector3();

  /** Record a spatial source's world position (called by `Experience` after the model loads,
   *  with each `spatial[].mesh` resolved to its world position). */
  setMeshPosition(meshName: string, pos: THREE.Vector3): void {
    this.meshPositions.set(meshName, pos.clone());
    for (const v of this.spatial) {
      if (v.cfg.mesh === meshName) {
        v.position.copy(pos);
        v.hasPosition = true;
        if (v.id !== null) v.howl.pos(pos.x, pos.y, pos.z, v.id);
      }
    }
  }

  /** Build + start every looping voice. Safe to call repeatedly (only runs once). */
  start(): void {
    if (this.started || !settings.audio.enabled || !settings.audio.scenes.enabled) return;
    this.started = true;
    const cfg = settings.audio.scenes;

    // Music beds — created paused at volume 0; the active one is faded up by setSection().
    for (const [key, rel] of Object.entries(cfg.loops)) {
      const howl = new Howl({ src: [encodeURI(cfg.base + rel)], loop: true, volume: 0, html5: false });
      this.loops.set(Number(key), howl);
    }

    // Spatial environmental loops — start playing immediately; the panner keeps them inaudible
    // until the camera is within range, so "plays only when near" falls out of the distance model.
    for (const sc of cfg.spatial) {
      const howl = new Howl({ src: [encodeURI(cfg.base + sc.file)], loop: true, volume: sc.volume, rate: sc.rate, html5: false });
      const voice: SpatialVoice = { howl, id: null, position: new THREE.Vector3(), hasPosition: false, cfg: sc };
      const pos = this.meshPositions.get(sc.mesh);
      if (pos) {
        voice.position.copy(pos);
        voice.hasPosition = true;
      }
      this.spatial.push(voice);
      const id = howl.play();
      voice.id = id;
      howl.pannerAttr(
        { panningModel: 'HRTF', distanceModel: 'inverse', refDistance: sc.refDistance, maxDistance: sc.maxDistance, rolloffFactor: sc.rolloff },
        id,
      );
      if (voice.hasPosition) howl.pos(voice.position.x, voice.position.y, voice.position.z, id);
    }

    // Wind — broad ambient bed (non-positional), faded in only while its scene is active.
    this.wind = new Howl({ src: [encodeURI(cfg.base + cfg.wind.file)], loop: true, volume: 0, html5: false });
    this.wind.play();

    this.log.info('Scene audio started (beds + spatial + wind).');
    if (this.activeSection > 0) this.applySection(this.activeSection);
  }

  /** Crossfade to the given chapter's bed (section index 1-5) + fade the wind by scene. */
  setSection(section: number): void {
    if (section === this.activeSection) return;
    this.activeSection = section;
    if (this.started) this.applySection(section);
  }

  private applySection(section: number): void {
    const cfg = settings.audio.scenes;
    // Restart (section 6) loops back to chapter 1's bed.
    const loopKey = this.loops.has(section) ? section : 1;
    for (const [key, howl] of this.loops) {
      const target = key === loopKey ? cfg.loopVolume : 0;
      howl.fade(howl.volume(), target, cfg.crossfadeMs);
    }
    if (this.wind) {
      const on = cfg.wind.scenes.includes(section);
      this.wind.fade(this.wind.volume(), on ? cfg.wind.volume : 0, cfg.crossfadeMs);
    }
  }

  /**
   * Fire the one-shot frame CUES (e.g. the Scene-1 doors opening) as the camera passes their
   * clip frame. Each fires once going forward and re-arms if the user scrubs back before it, so
   * scrolling up and down doesn't machine-gun it.
   */
  checkCues(frame: number): void {
    if (!this.started) return;
    const cues = settings.audio.scenes.cues;
    for (let i = 0; i < cues.length; i += 1) {
      const cue = cues[i]!;
      const armed = this.cueArmed[i] ?? true;
      if (!armed) {
        if (frame < cue.frame - 2) this.cueArmed[i] = true; // scrubbed back → re-arm
        continue;
      }
      if (frame >= cue.frame) {
        this.cueArmed[i] = false;
        this.playCue(cue.key);
      }
    }
  }

  /** Play a frame cue by key (also used by the debug GUI's test button). Lazily loads it. */
  playCue(key: string): void {
    const cue = settings.audio.scenes.cues.find((c) => c.key === key);
    if (!cue || !settings.audio.enabled) return;
    let howl = this.cueHowls.get(key);
    if (!howl) {
      howl = new Howl({
        src: [encodeURI(settings.audio.scenes.base + cue.file)],
        volume: cue.volume,
        rate: cue.rate,
        html5: false,
      });
      this.cueHowls.set(key, howl);
    }
    howl.volume(cue.volume);
    howl.rate(cue.rate);
    howl.play();
  }

  /** Play the transition swell under the white flash (75%, fades, sped toward the flash length). */
  playTransition(): void {
    if (!this.started) return;
    const cfg = settings.audio.scenes.transition;
    if (!this.transition) {
      this.transition = new Howl({
        src: [encodeURI(settings.audio.scenes.base + cfg.file)],
        volume: 0,
        html5: false,
      });
    }
    this.transition.rate(cfg.rate);
    const id = this.transition.play();
    this.transition.fade(0, cfg.volume, cfg.fadeMs, id);
  }

  /** Move the Howler LISTENER to the camera each frame — orientation (forward + up) so the
   *  spatial panning tracks where the viewer is actually looking. */
  updateListener(camera: THREE.Camera): void {
    if (!this.started) return;
    camera.getWorldPosition(this._pos);
    Howler.pos(this._pos.x, this._pos.y, this._pos.z);
    camera.getWorldDirection(this._fwd);
    this._up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    Howler.orientation(this._fwd.x, this._fwd.y, this._fwd.z, this._up.x, this._up.y, this._up.z);
  }

  dispose(): void {
    for (const h of this.loops.values()) h.unload();
    this.loops.clear();
    for (const v of this.spatial) v.howl.unload();
    this.spatial.length = 0;
    this.wind?.unload();
    this.transition?.unload();
    for (const h of this.cueHowls.values()) h.unload();
    this.cueHowls.clear();
  }
}
